import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  UserPlusIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilIcon,
  HomeIcon,
  UserGroupIcon,
  CogIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import { isAuthenticated } from "../services/authService";

function InvestorAccessPage() {
  const [investors, setInvestors] = useState([]);
  const [investorUsers, setInvestorUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    investorId: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [investorSearchTerm, setInvestorSearchTerm] = useState("");
  const [showInvestorDropdown, setShowInvestorDropdown] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [debugInfo, setDebugInfo] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }

    console.log("InvestorAccessPage mounted, starting data fetch...");
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    console.log("fetchData called - starting...");
    setError("");
    setSuccess("");
    setDebugInfo("Starting data fetch...");

    try {
      console.log(
        "Auth token:",
        localStorage.getItem("token") ? "Present" : "Missing"
      );

      // Get investors data with 403 handling
      let investorsArray = [];
      try {
        setDebugInfo("Fetching investors data...");
        // Fetch ALL investors by setting a very high limit to avoid pagination issues
        // This ensures all 6,574+ investors are available for access management
        const investorsRes = await api.get(
          "/api/investors?limit=999999&page=1"
        );

        console.log("Investors response:", investorsRes.data);

        // Handle response structure - the backend returns {data: [...], pagination: {...}}
        if (investorsRes.data?.data && Array.isArray(investorsRes.data.data)) {
          // Backend returns paginated response with data property
          investorsArray = investorsRes.data.data;
        } else if (
          investorsRes.data?.investors &&
          Array.isArray(investorsRes.data.investors)
        ) {
          // Alternative structure with investors property
          investorsArray = investorsRes.data.investors;
        } else if (Array.isArray(investorsRes.data)) {
          // Direct array response
          investorsArray = investorsRes.data;
        } else {
          console.warn(
            "Unexpected investors response structure:",
            investorsRes.data
          );
          investorsArray = [];
        }

        console.log("Final investorsArray:", investorsArray.length, "items");
      } catch (investorsError) {
        if (investorsError.response?.status !== 403) {
          console.error("Failed to fetch investors:", investorsError);
        }
        // Silently fail for 403 - show empty list
      }
      setInvestors(investorsArray);

      // Get investor users with error handling
      try {
        setDebugInfo("Fetching investor users...");
        const usersRes = await api.get("/api/auth/investor-users");
        console.log("Users response:", usersRes.data);

        const usersArray = Array.isArray(usersRes.data) ? usersRes.data : [];
        setInvestorUsers(usersArray);

        if (usersArray.length === 0) {
          setDebugInfo(
            `Successfully loaded ${investorsArray.length} investors. No investor access has been granted yet - this is normal for a new system.`
          );
        } else {
          setDebugInfo(
            `Successfully loaded ${investorsArray.length} investors and ${usersArray.length} investor accounts with access.`
          );
        }
      } catch (usersError) {
        console.warn("Investor users API error:", usersError);

        // Check if it's a server error vs empty data
        if (usersError.response?.status === 500) {
          setError(
            "Failed to load investor users from database. Please check if the backend server is running and the database is accessible."
          );
          setDebugInfo(`Database error: ${usersError.message}`);
        } else if (usersError.response?.status === 401) {
          setError("Authentication failed. Please log in again.");
          navigate("/login");
          return;
        } else {
          // For other errors, still set empty array but with better messaging
          setInvestorUsers([]);
          setDebugInfo(
            `Loaded ${
              investorsArray.length
            } investors. Could not fetch investor users (${
              usersError.response?.status || "Network error"
            }), but this might be normal if no access has been granted yet.`
          );
        }
      }

      console.log("fetchData completed successfully");
    } catch (error) {
      console.error("fetchData error:", error);

      if (error.message?.includes("Authentication failed")) {
        setError(error.message);
        navigate("/login");
        return;
      }

      setError(error.message || "Failed to load data");
      setDebugInfo(`Error: ${error.message}`);

      // Set fallbacks
      setInvestors([]);
      setInvestorUsers([]);
    }

    // Always set loading to false at the end
    console.log("Setting loading to false");
    setLoading(false);
  };

  const handleCreateAccess = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    try {
      await api.post("/api/auth/create-investor-access", {
        investorId: formData.investorId,
        username: formData.username,
        password: formData.password,
      });

      setSuccess("Investor access created successfully");
      setFormData({
        investorId: "",
        username: "",
        password: "",
        confirmPassword: "",
      });
      setInvestorSearchTerm("");
      setShowInvestorDropdown(false);
      setShowCreateForm(false);
      fetchData();
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to create investor access"
      );
    }
  };

  const handleDeleteAccess = async (userId) => {
    if (
      !window.confirm("Are you sure you want to remove this investor's access?")
    ) {
      return;
    }

    try {
      await api.delete(`/api/auth/investor-users/${userId}`);
      setSuccess("Investor access removed successfully");
      fetchData();
    } catch (error) {
      setError("Failed to remove investor access");
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = window.prompt(
      "Enter new password (minimum 6 characters):"
    );
    if (!newPassword) return;

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    try {
      await api.put(`/api/auth/investor-users/${userId}/reset-password`, {
        password: newPassword,
      });
      setSuccess("Password reset successfully");
    } catch (error) {
      setError("Failed to reset password");
    }
  };

  const filteredInvestorUsers = Array.isArray(investorUsers)
    ? investorUsers.filter(
        (user) =>
          user.investor_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          user.username?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const availableInvestors = Array.isArray(investors)
    ? investors.filter(
        (investor) =>
          !investorUsers.some((user) => user.investor_id === investor.id)
      )
    : [];

  // Filter available investors based on search term in the modal
  const filteredAvailableInvestors = availableInvestors.filter(
    (investor) =>
      investor.buyer_name?.toLowerCase().includes(investorSearchTerm.toLowerCase()) ||
      investor.mobile_number?.includes(investorSearchTerm)
  );

  // Get selected investor details for display
  const selectedInvestor = availableInvestors.find(
    (inv) => inv.id === parseInt(formData.investorId)
  );

  console.log("Render - loading:", loading);
  console.log("Render - investors:", investors.length);
  console.log("Render - investorUsers:", investorUsers.length);
  console.log("Render - availableInvestors:", availableInvestors.length);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600 text-lg">
                    Loading investor access data...
                  </p>
                  {debugInfo && (
                    <p className="mt-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                      Status: {debugInfo}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    Loading investors and checking who has access to the
                    investor dashboard
                  </p>
                  <div className="mt-4 space-x-2">
                    <button
                      onClick={() => {
                        console.log(
                          "Force refresh clicked - bypassing loading"
                        );
                        setLoading(false);
                        setError("");
                        setDebugInfo(
                          "Loading bypassed manually - this may show incomplete data"
                        );
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      Continue Anyway
                    </button>
                    <button
                      onClick={() => {
                        console.log("Retry Loading clicked - restarting fetch");
                        setLoading(true);
                        setError("");
                        setDebugInfo("Retrying data fetch...");
                        fetchData();
                      }}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors duration-200"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Enhanced Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>•</span>
                <span>Investor Access Management</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <UserPlusIcon className="h-5 w-5" />
                Grant Access
              </button>
              <div className="bg-green-50 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-green-700">
                  Access Control
                </span>
              </div>
            </div>
          </div>

          {/* Page Title and Description */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <CogIcon className="h-8 w-8 text-gray-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                Investor Access Management
              </h1>
            </div>
            <p className="text-gray-600 max-w-4xl">
              Manage login access for investors to view their personalized
              dashboard. Grant access, manage credentials, and control who can
              access the investor portal with comprehensive user management
              tools.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                🔐 Access Control
              </span>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                👥 User Management
              </span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                🔍 Live Search
              </span>
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                🛡️ Secure Access
              </span>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Access Overview
              </h3>
              <div className="ml-4 text-sm text-gray-500">
                Real-time investor access statistics
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-blue-100 border border-blue-200 rounded-lg flex items-center justify-center">
                    <UserGroupIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900 mb-1">
                      {investors.length.toLocaleString()}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Total Investors
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl shadow-sm hover:shadow-md hover:border-green-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-green-100 border border-green-200 rounded-lg flex items-center justify-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900 mb-1">
                      {investorUsers.length.toLocaleString()}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Access Granted
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-amber-100 border border-amber-200 rounded-lg flex items-center justify-center">
                    <KeyIcon className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900 mb-1">
                      {availableInvestors.length.toLocaleString()}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Available
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-purple-100 border border-purple-200 rounded-lg flex items-center justify-center">
                    <CogIcon className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900 mb-1">
                      {(
                        (investorUsers.length / Math.max(investors.length, 1)) *
                        100
                      ).toFixed(0)}
                      %
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Access Rate
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Debug Information - Only show if there are issues or in development */}
        {(error ||
          debugInfo.includes("Error") ||
          debugInfo.includes("Failed")) && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-900">
                  System Status
                </h3>
                <div className="mt-2 text-xs text-yellow-700">
                  <p>Loading: {loading ? "true" : "false"}</p>
                  <p>Investors Count: {investors.length}</p>
                  <p>Investor Users Count: {investorUsers.length}</p>
                  <p>Available Investors: {availableInvestors.length}</p>
                  <p>Status: {debugInfo}</p>
                  {error && <p className="text-red-600">Error: {error}</p>}
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => {
                      setError("");
                      setDebugInfo("");
                      fetchData();
                    }}
                    className="text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded"
                  >
                    Retry Loading
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Status - Show briefly when everything is loaded successfully */}
        {!loading &&
          !error &&
          investors.length > 0 &&
          debugInfo.includes("Successfully") && (
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">
                        ✓ Loaded {investors.length} investors
                        {investorUsers.length > 0 &&
                          ` • ${investorUsers.length} have dashboard access`}
                        {investorUsers.length === 0 &&
                          " • No dashboard access granted yet"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Messages */}
        {error && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {success && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                  <p className="text-green-800 font-medium">{success}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Access Form */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Grant Investor Access</h2>
              <form onSubmit={handleCreateAccess} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Investor
                  </label>
                  <div className="relative">
                    {/* Search Input */}
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={investorSearchTerm}
                        onChange={(e) => {
                          setInvestorSearchTerm(e.target.value);
                          setShowInvestorDropdown(true);
                        }}
                        onFocus={() => setShowInvestorDropdown(true)}
                        placeholder="Search by name or mobile number..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    {/* Selected Investor Display */}
                    {selectedInvestor && !showInvestorDropdown && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-blue-900">
                            {selectedInvestor.buyer_name}
                          </span>
                          <span className="text-sm text-blue-600 ml-2">
                            - {selectedInvestor.mobile_number}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, investorId: "" });
                            setInvestorSearchTerm("");
                            setShowInvestorDropdown(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Change
                        </button>
                      </div>
                    )}

                    {/* Dropdown List */}
                    {showInvestorDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredAvailableInvestors.length > 0 ? (
                          <>
                            <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b sticky top-0">
                              {filteredAvailableInvestors.length} investor{filteredAvailableInvestors.length !== 1 ? 's' : ''} found
                              {investorSearchTerm && ` for "${investorSearchTerm}"`}
                            </div>
                            {filteredAvailableInvestors.slice(0, 100).map((investor) => (
                              <div
                                key={investor.id}
                                onClick={() => {
                                  setFormData({ ...formData, investorId: investor.id.toString() });
                                  setInvestorSearchTerm("");
                                  setShowInvestorDropdown(false);
                                }}
                                className={`px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${
                                  formData.investorId === investor.id.toString() ? 'bg-blue-100' : ''
                                }`}
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  {investor.buyer_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {investor.mobile_number}
                                  {investor.scheme_name && ` • ${investor.scheme_name}`}
                                </div>
                              </div>
                            ))}
                            {filteredAvailableInvestors.length > 100 && (
                              <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 text-center">
                                Showing first 100 results. Type more to narrow down.
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            {investorSearchTerm 
                              ? `No investors found matching "${investorSearchTerm}"`
                              : "No available investors"
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Click outside to close dropdown */}
                  {showInvestorDropdown && (
                    <div 
                      className="fixed inset-0 z-0" 
                      onClick={() => setShowInvestorDropdown(false)}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter username"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                      placeholder="Enter password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        confirmPassword: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm password"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setInvestorSearchTerm("");
                      setShowInvestorDropdown(false);
                    }}
                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Access
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Enhanced Search Section */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Search Investor Access
              </h3>
              <div className="ml-4 text-sm text-gray-500">
                Find investors by name or username
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by investor name or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {/* Main Content - Investor Users Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <KeyIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Investors with Access
              </h3>
              <div className="ml-4 text-sm text-gray-500">
                ({filteredInvestorUsers.length}{" "}
                {filteredInvestorUsers.length === 1 ? "user" : "users"} with
                dashboard access)
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Investor Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scheme
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvestorUsers.length > 0 ? (
                    filteredInvestorUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.investor_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.mobile_number}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <KeyIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {user.username}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {user.scheme_name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleResetPassword(user.id)}
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                              title="Reset Password"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAccess(user.id)}
                              className="text-red-600 hover:text-red-900 flex items-center"
                              title="Remove Access"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="space-y-3">
                          <KeyIcon className="h-12 w-12 text-gray-300 mx-auto" />
                          <div>
                            <p className="text-gray-500 font-medium">
                              {searchTerm
                                ? "No investor users found matching your search"
                                : "No investor access has been granted yet"}
                            </p>
                            {!searchTerm && (
                              <p className="text-sm text-gray-400 mt-2">
                                Click "Grant Access" above to give an investor
                                login access to their dashboard
                              </p>
                            )}
                          </div>
                          {!searchTerm && availableInvestors.length > 0 && (
                            <button
                              onClick={() => setShowCreateForm(true)}
                              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <UserPlusIcon className="h-4 w-4 mr-2" />
                              Grant Access to First Investor
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            {investorUsers.length === 0 && investors.length > 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <KeyIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-900">
                      Ready to Grant Investor Access
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p className="mb-2">
                        You have {investors.length} investors in your database,
                        but none have been granted dashboard access yet.
                      </p>
                      <p className="mb-2">
                        <strong>What you can do:</strong>
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          Click "Grant Access" above to give an investor login
                          credentials
                        </li>
                        <li>They can then log in at the investor login page</li>
                        <li>
                          Investors will see their personal dashboard with
                          transaction history
                        </li>
                        <li>
                          You can manage their access, reset passwords, or
                          revoke access anytime
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <KeyIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-900">
                      How Investor Access Works
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          Grant login access to investors using their existing
                          investor record
                        </li>
                        <li>
                          Investors can log in with their username and password
                        </li>
                        <li>
                          They will see a dashboard with their transaction
                          history and investment details
                        </li>
                        <li>
                          Access can be revoked or passwords can be reset as
                          needed
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestorAccessPage;
